import React, { useEffect, useState } from "react";
import { Controller, setValue } from "react-hook-form";
import { useSelector } from "react-redux";
import { SelectCustom } from "../../components/ui/SelectCustom";

// Attendee-name guards (2026-09-29). A PAT certificate went out to "apd ltd apd ltd"
// because the customer typed the company into BOTH name fields. The name printed on
// the certificate must be the person's own name; the company belongs in the optional
// "Company Name" field on the billing step.
//  - Company words are matched as WHOLE words (so "Cole", "Coleman", "Grouper",
//    "Serviceson" all pass) with Unicode-aware edges (so an accented neighbour such as
//    "Coëlho" is not split into "Co" + "ëlho"). Hyphens, apostrophes, spaces and
//    two-word surnames pass.
//  - Any digit is rejected (no real name contains one; "Unit 4", "3M" etc. do).
//  - First name equal to last name (trimmed, case-insensitive) is rejected.
const COMPANY_WORD_RE =
  /(^|[^\p{L}\p{M}\p{N}_])(ltd|limited|plc|llp|inc|company|group|services|solutions)(?=$|[^\p{L}\p{M}\p{N}_])/iu;
const DIGIT_RE = /\d/;
export const COMPANY_NAME_MSG =
  "Enter the attendee's own name - the company goes in Company Name";
export const SAME_NAME_MSG =
  "First name and last name look the same - please check";

const normName = (v) => String(v == null ? "" : v).trim().toLowerCase();

// Returns true when valid, false for blank (falls back to the "Enter ... please"
// default text), or an error message string.
export const validatePersonName = (value) => {
  const v = normName(value);
  if (!v) return false;
  if (DIGIT_RE.test(v) || COMPANY_WORD_RE.test(v)) return COMPANY_NAME_MSG;
  return true;
};

export const namesLookTheSame = (first, last) => {
  const a = normName(first).replace(/\s+/g, " ");
  const b = normName(last).replace(/\s+/g, " ");
  return !!a && a === b;
};

export const PersonalDetailsForm = ({
  register,
  control,
  errors,
  touchedFields,
  index = null,
  learner,
  unregister,
  watch,
  setValue, trigger,
  handleValidatePhone,
}) => {
  const genderOptions = [
    {
      value: "male",
      label: "Mr",
    },
    {
      value: "female",
      label: "Ms",
    },
  ];

  useEffect(() => {
    return () => {
      unregister(`title${index ? "-" + index : ""}`);
      unregister(`firstName${index ? "-" + index : ""}`);
      unregister(`lastName${index ? "-" + index : ""}`);
      unregister(`emailAddress${index ? "-" + index : ""}`);
      unregister(`phone${index ? "-" + index : ""}`);
    };
  }, [learner]);

  const phoneFieldName = `phone${index ? "-" + index : ""}`;
  const firstNameField = `firstName${index ? "-" + index : ""}`;
  const lastNameField = `lastName${index ? "-" + index : ""}`;
  const phoneValue = watch(phoneFieldName) || "";

  // Positive inline feedback: a field shows a green tick once it has been
  // blurred (touched), has a value, and has no validation error.
  const vName = (base) => `${base}${index ? "-" + index : ""}`;
  const isFieldValid = (base) => {
    const n = vName(base);
    return !!(touchedFields && touchedFields[n] && !errors?.[n] && watch(n));
  };

  // index 0 (or null) = the booker / billing contact -> email required.
  // index >= 1 = a learner -> email OPTIONAL (blank routes their paperwork to the booker,
  // who is copied on everything anyway). 2026-07-10.
  const isLearner = !!index && index > 0;
  const emailFieldName = `emailAddress${index ? "-" + index : ""}`;
  const [bookerClash, setBookerClash] = useState(false);
  const [dupeWarn, setDupeWarn] = useState(false);
  // The booker's email lives in a DIFFERENT react-hook-form instance (step 1),
  // so watch("emailAddress") is undefined on the Add Learner step. Read the
  // committed step-1 value from the redux booking state instead.
  const bookerEmailFromStore = useSelector(
    (s) =>
      (s.courses.learners && s.courses.learners[0] && s.courses.learners[0].emailAddress) || "",
  );


  useEffect(() => {
    const current = watch(phoneFieldName);
    if (!current && learner?.phone) {
      setValue(phoneFieldName, learner.phone);
    }
  }, []);

  return (
    <div className="row gutters-20 mb-30">
      <div className="col-12">
        <div className="d-flex">
          <div className="flex-80 mr-10">
            <div className="form-group">
              <label className="form-group-title">
                Title
                <span className="color-danger ms-1">*</span>
              </label>
              <Controller
                name={`title${index ? "-" + index : ""}`}
                control={control}
                render={({ field: { onChange, onBlur, ref } }) => (
                  <SelectCustom
                    options={genderOptions}
                    onBlur={onBlur}
                    onSelect={(selectedOption) => {
                      onChange(selectedOption);
                    }}
                    ref={ref}
                  />
                )}
                defaultValue={
                  learner?.title ? learner?.title : genderOptions[0].value
                }
              />
            </div>
          </div>
          <div className="flex-auto">
            <div className="form-group">
              <label className="form-group-title">
                First Name
                <span className="color-danger ms-1">*</span>
              </label>
              <input
                className={`input ${
                  errors?.firstName || errors?.[`firstName-${index}`]
                    ? "error"
                    : isFieldValid("firstName")
                    ? "is-valid"
                    : ""
                }`}
                type="text"
                autoComplete="given-name"
                {...register(firstNameField, {
                  required: true,
                  validate: (value) => validatePersonName(value),
                  onChange: () => {
                    // Keep the "first = last" check on the Last Name field live:
                    // re-check it once the customer has already been there.
                    if (
                      watch(lastNameField) &&
                      (touchedFields?.[lastNameField] || errors?.[lastNameField])
                    ) {
                      trigger(lastNameField);
                    }
                  },
                })}
                defaultValue={learner?.firstName && learner.firstName}
                placeholder="John"
              />
              {/*{errors.firstName && (*/}
              {/*  <span className="color-danger">*/}
              {/*    {errors.firstName.message || "Enter First name please"}*/}
              {/*  </span>*/}
              {/*)}*/}
              {(errors?.firstName || errors?.[`firstName-${index}`]) ? (
                  <span className="color-danger mb-m-24">
                    {errors?.firstName?.message || errors?.[`firstName-${index}`]?.message || "Enter First name please"}
                  </span>
              ) : null}
            </div>
          </div>
        </div>
      </div>
      <div className="col-12">
        <div className="form-group">
          <label className="form-group-title">
            Last Name
            <span className="color-danger ms-1">*</span>
          </label>
          <input
            className={`input ${
              errors?.lastName || errors?.[`lastName-${index}`]
                ? "error"
                : isFieldValid("lastName")
                ? "is-valid"
                : ""
            }`}
            type="text"
            autoComplete="family-name"
            required=""
            {...register(lastNameField, {
              required: true,
              validate: (value, formValues) => {
                const ok = validatePersonName(value);
                if (ok !== true) return ok;
                const first =
                  (formValues && formValues[firstNameField]) ||
                  watch(firstNameField);
                if (namesLookTheSame(first, value)) return SAME_NAME_MSG;
                return true;
              },
            })}
            placeholder="Doe"
            defaultValue={learner?.lastName && learner.lastName}
          />
          {(errors?.lastName || errors?.[`lastName-${index}`]) ? (
              <span className="color-danger mb-m-24">
              {errors?.lastName?.message || errors?.[`lastName-${index}`]?.message || "Enter Last Name please"}
            </span>
          ) : null}
        </div>
      </div>
      <div className="col-12">
        <div className="form-group">
          <label className="form-group-title">
            Email
            {isLearner ? (
              <span className="ms-1" style={{ fontWeight: 400, color: "#5c6b7b" }}>
                (optional)
              </span>
            ) : (
              <span className="color-danger ms-1">*</span>
            )}
          </label>
          <input
            className={`input ${
              errors?.emailAddress || errors?.[`emailAddress-${index}`]
                ? "error"
                : isFieldValid("emailAddress")
                ? "is-valid"
                : ""
            }`}
            type="text"
            autoComplete="email"
            inputMode="email"
            {...register(emailFieldName, {
              // booker: required. learner: optional, but if entered it must be valid.
              required: isLearner ? false : true,
              validate: (value) => {
                if (isLearner && (!value || !value.trim())) return true; // blank ok for a learner
                return /\S+@\S+\.\S+/.test(value) || "Enter valid email please";
              },
              onChange: (e) => {
                if (!isLearner) return e.target.value;
                const value = e.target.value;
                const all = watch();
                const booker = ((all.emailAddress || bookerEmailFromStore) || "")
                  .trim()
                  .toLowerCase();
                const v = (value || "").trim().toLowerCase();
                // A learner who typed the BOOKER's own email: clear it. With the booker
                // copied on everything, their address here delivers the same as blank -
                // so we keep it off the learner's record (no wrong-name certificate).
                if (v && v === booker) {
                  setValue(emailFieldName, "");
                  setBookerClash(true);
                  setDupeWarn(false);
                  return "";
                }
                setBookerClash(false);
                // Soft warning only (never blocks) when it matches ANOTHER learner.
                const clash = Object.keys(all).some(
                  (k) =>
                    k.startsWith("emailAddress") &&
                    k !== emailFieldName &&
                    k !== "emailAddress" &&
                    v !== "" &&
                    (all[k] || "").trim().toLowerCase() === v
                );
                setDupeWarn(clash);
                return value;
              },
            })}
            placeholder={
              isLearner
                ? "Leave blank — everything comes to you"
                : "john.doe@example.com"
            }
            defaultValue={learner?.emailAddress && learner.emailAddress}
          />
          {(errors?.emailAddress || errors?.[`emailAddress-${index}`]) ? (
            <span className="color-danger mb-m-24">
              {errors?.emailAddress?.message ||
                errors?.[`emailAddress-${index}`]?.message ||
                  "Enter email please"
              }
            </span>
          ) : null}
          {isLearner && !bookerClash && !dupeWarn ? (
            <span
              className="d-block mt-2"
              style={{
                fontSize: "13px",
                color: "#155e64",
                background: "#e5f4f5",
                borderLeft: "3px solid #1f97a0",
                borderRadius: "0 6px 6px 0",
                padding: "7px 10px",
                lineHeight: 1.45,
              }}
            >
              <strong>You get a copy of everything.</strong> As the booker you
              automatically receive this learner&rsquo;s joining instructions and
              certificate. Add their own email to have their certificate reach them
              directly too, or leave it blank.
            </span>
          ) : null}
          {isLearner && bookerClash ? (
            <span
              className="d-block mt-2"
              style={{
                fontSize: "13px",
                color: "#7a5a12",
                background: "#fff7e8",
                border: "1px solid #e8b64a",
                borderRadius: "6px",
                padding: "7px 10px",
                lineHeight: 1.45,
              }}
            >
              <strong>That&rsquo;s your email, so we&rsquo;ve cleared it.</strong> You
              already get this learner&rsquo;s paperwork as the booker &mdash; no need
              to enter your own address. Add the learner&rsquo;s own email if they have
              one.
            </span>
          ) : null}
          {isLearner && dupeWarn ? (
            <span
              className="d-block mt-2"
              style={{
                fontSize: "13px",
                color: "#7a5a12",
                background: "#fff7e8",
                border: "1px solid #e8b64a",
                borderRadius: "6px",
                padding: "7px 10px",
                lineHeight: 1.45,
              }}
            >
              <strong>This email is already used for another learner.</strong>{" "}
              Certificates are emailed per person, so both would arrive in the same
              inbox. That&rsquo;s fine if they share it &mdash; or leave one blank and
              it comes to you.
            </span>
          ) : null}
        </div>
      </div>
      <div className="col-12">
        <div className="form-group">
          <label className="form-group-title">
            Phone Number
            <span className="color-danger ms-1">*</span>
          </label>

          <input
              type="tel"
              autoComplete="tel"
              placeholder="e.g. 07123 456789"
              {...register(phoneFieldName, {
                required: "Phone is required",
                minLength: {
                  value: 7,
                  message: "Please enter a valid phone number",
                },
                maxLength: {
                  value: 18,
                  message: "Please enter a valid phone number",
                },
                pattern: {
                  value: /^\+?[0-9\s]{7,18}$/,
                  message: "Please enter a valid phone number",
                },
                onChange: (e) => {
                  // Keep digits, spaces and a single leading "+"; do NOT force-prepend a
                  // "0" or truncate the last digit (that corrupted UK/intl/autofilled
                  // numbers and blocked checkout). The backend (libphonenumber GB) is the
                  // source of truth and is checked onBlur.
                  let v = e.target.value.replace(/[^\d+\s]/g, "");
                  if (v.indexOf("+") > 0) v = v.replace(/\+/g, "");
                  setValue(phoneFieldName, v);
                },
              })}
              value={phoneValue}
              className={`input ${
                  errors?.phone || errors?.[`phone-${index}`]
                    ? "error"
                    : isFieldValid("phone")
                    ? "is-valid"
                    : ""
              }`}
              onBlur={(e) => handleValidatePhone(e.target.value, phoneFieldName)}
          />
          {(errors?.phone || errors?.[`phone-${index}`])?.message?.length ? (
            <span className="color-danger mb-m-24">
              {errors?.phone?.message || errors?.[`phone-${index}`].message}
            </span>
          ) : null}
        </div>
      </div>
    </div>
  );
};
